export const DRAFT_STORAGE_KEY = 'RaiseEnquiryDraft';
export const MIN_DESCRIPTION_LENGTH = 5;
export const RAISE_ENQUIRY_OUTCOME = 'RaiseEnquiry';
export const SELF_SERVE_OUTCOME = 'SelfServe';
export const ACCOUNT_QUERY_CHOICE = 'account-query';
export const ACCOUNT_QUERY_LABEL = 'Account query';

export const STEPS = [
    { number: 1, label: 'Utility' },
    { number: 2, label: 'Query type' },
    { number: 3, label: 'Option' },
    { number: 4, label: 'Details' },
];

export function createSubmissionReference() {
    const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();

    const bytes = new Uint8Array(16);
    if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
        cryptoApi.getRandomValues(bytes);
    } else {
        for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

export function isDescriptionValid(description) {
    return typeof description === 'string' && description.trim().length >= MIN_DESCRIPTION_LENGTH;
}

function byDisplayOrder(a, b) {
    return (a.displayOrder || 0) - (b.displayOrder || 0);
}

export function getUtilities(structure) {
    const utilities = Array.isArray(structure?.utilities) ? structure.utilities : [];
    return [...utilities].sort(byDisplayOrder);
}

export function getQueryTypes(structure, utilityDomainId) {
    const utility = getUtilities(structure).find((u) => u.domainId === utilityDomainId);
    const queryTypes = Array.isArray(utility?.queryTypes) ? utility.queryTypes : [];
    return [...queryTypes].sort(byDisplayOrder);
}

export function getOptions(structure, utilityDomainId, queryTypeDomainId) {
    const queryType = getQueryTypes(structure, utilityDomainId).find((q) => q.domainId === queryTypeDomainId);
    const options = Array.isArray(queryType?.options) ? queryType.options : [];
    return [...options].sort(byDisplayOrder);
}

export function findSelection(structure, draft) {
    const utility = getUtilities(structure).find((u) => u.domainId === draft?.utilityDomainId) || null;
    const queryType = utility
        ? getQueryTypes(structure, utility.domainId).find((q) => q.domainId === draft?.queryTypeDomainId) || null
        : null;
    const option = queryType
        ? getOptions(structure, utility.domainId, queryType.domainId).find((o) => o.domainId === draft?.optionDomainId) || null
        : null;
    return { utility, queryType, option };
}

export function isRaiseEnquiryOption(option) {
    return option?.outcome === RAISE_ENQUIRY_OUTCOME;
}

export const SELF_SERVE_FALLBACK_ROUTE = '/dashboard/main';

export const SELF_SERVE_DESTINATIONS = {
    PaymentPage: {
        url: SELF_SERVE_FALLBACK_ROUTE,
        external: false,
        ctaLabel: 'Go to your dashboard',
        guidance: 'Making a payment is quickest from your dashboard — open your Water account and choose Pay now.',
    },
    DirectDebitSetupPage: {
        url: SELF_SERVE_FALLBACK_ROUTE,
        external: false,
        ctaLabel: 'Go to your dashboard',
        guidance: 'You can set up or reinstate your Direct Debit from your dashboard — open your account and go to Your details.',
    },
    MeterReadPage: {
        url: '/dashboard/meters',
        external: false,
        ctaLabel: 'Go to your meters page',
        guidance: 'Submitting a meter reading is quickest through your meters page — no need to wait for our team.',
    },
    BillingFaqs: {
        url: null,
        external: true,
        ctaLabel: 'Go to help and FAQs',
        guidance: 'Our help and FAQs section answers most billing questions straight away.',
    },
    PortalInvoicePage: {
        url: '/Billing',
        external: false,
        ctaLabel: 'Go to your billing page',
        guidance: 'Your bills, statements and transactions are ready to view and download on your billing page.',
    },
    WasteInvoicePage: {
        url: SELF_SERVE_FALLBACK_ROUTE,
        external: false,
        ctaLabel: 'Go to your dashboard',
        guidance: 'Your waste bills and additional charges are on your dashboard — open your Waste account.',
    },
};

export const SELF_SERVE_FALLBACK_DESTINATION = {
    url: SELF_SERVE_FALLBACK_ROUTE,
    external: false,
    ctaLabel: 'Go to your dashboard',
    guidance: 'You can do this yourself from your main dashboard.',
};

export function resolveSelfServeDestination(option, faqLink) {
    if (!option || isRaiseEnquiryOption(option)) return null;
    const pageKey = option.selfServePage?.pageKey;
    const destination = (pageKey && SELF_SERVE_DESTINATIONS[pageKey]) || SELF_SERVE_FALLBACK_DESTINATION;
    if (destination.external) {
        if (!faqLink) return { ...SELF_SERVE_FALLBACK_DESTINATION };
        return { ...destination, url: faqLink };
    }
    return { ...destination };
}

export function createDraft() {
    return {
        utilityDomainId: null,
        queryTypeDomainId: null,
        optionDomainId: null,
        description: '',
        siteDomainId: null,
        accountQuery: false,
        submissionReference: createSubmissionReference(),
        documents: [],
    };
}

export function getSites(sitesData) {
    return Array.isArray(sitesData?.sites) ? sitesData.sites : [];
}

export function siteChoiceRequired(sites) {
    return Array.isArray(sites) && sites.length > 1;
}

export function applySiteChoice(draft, value) {
    if (value === ACCOUNT_QUERY_CHOICE) return { ...draft, siteDomainId: null, accountQuery: true };
    return { ...draft, siteDomainId: value || null, accountQuery: false };
}

export function reconcileDraftSite(draft, sites) {
    if (!draft.siteDomainId) return draft;
    if ((sites || []).some((site) => site.siteDomainId === draft.siteDomainId)) return draft;
    return { ...draft, siteDomainId: null };
}

export function resolveSiteChoice(sites, draft) {
    if (!Array.isArray(sites)) return null;
    if (sites.length === 0) return { siteDomainId: null, accountQuery: true };
    if (sites.length === 1) return { siteDomainId: sites[0].siteDomainId, accountQuery: false };
    if (draft.accountQuery) return { siteDomainId: null, accountQuery: true };
    const chosenSite = sites.find((site) => site.siteDomainId === draft.siteDomainId);
    return chosenSite ? { siteDomainId: chosenSite.siteDomainId, accountQuery: false } : null;
}

export function autoSiteName(sites) {
    if (!Array.isArray(sites) || siteChoiceRequired(sites)) return null;
    return sites.length === 1 ? sites[0].siteName : ACCOUNT_QUERY_LABEL;
}

export function applyUtilityChoice(draft, utilityDomainId) {
    if (draft.utilityDomainId === utilityDomainId) return draft;
    return { ...draft, utilityDomainId, queryTypeDomainId: null, optionDomainId: null };
}

export function applyQueryTypeChoice(draft, queryTypeDomainId) {
    if (draft.queryTypeDomainId === queryTypeDomainId) return draft;
    return { ...draft, queryTypeDomainId, optionDomainId: null };
}

export function applyOptionChoice(draft, optionDomainId) {
    return { ...draft, optionDomainId };
}

export function initialStepFor(draft, structure) {
    const { utility, option } = findSelection(structure, draft);
    if (option) return 3;
    if (utility) return 2;
    return 1;
}

export function loadDraft(storage) {
    try {
        const raw = storage.getItem(DRAFT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || !parsed.submissionReference) return null;
        return { ...createDraft(), ...parsed, submissionReference: parsed.submissionReference };
    } catch (err) {
        return null;
    }
}

export function saveDraft(storage, draft) {
    try {
        storage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (err) {
        return;
    }
}

export function clearDraft(storage) {
    try {
        storage.removeItem(DRAFT_STORAGE_KEY);
    } catch (err) {
        return;
    }
}
